// Globals
import { FC } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

// Modules
import { selectChannelSlug } from '@/modules/Channels';

// Models
import { GoogleMerchantSyncRunMode } from '@/models/googleMerchantSync.model';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  selectGoogleMerchantSyncIsLoading,
  syncGoogleMerchantDelta,
  syncGoogleMerchantFull,
  syncGoogleMerchantProduct,
} from '../../feature';

// Components
import { Modal, ModalSize } from '@/components';
import GoogleMerchantSyncForm from '../GoogleMerchantSyncForm/GoogleMerchantSyncForm';

// Helpers
import { GoogleMerchantSyncFormFields, TGoogleMerchantSyncFormValues } from '../../helpers';

type TGoogleMerchantSyncModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const GoogleMerchantSyncModal: FC<TGoogleMerchantSyncModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const channelSlug = useAppState(selectChannelSlug);
  const isLoading = useAppState(selectGoogleMerchantSyncIsLoading);

  const handleRunSync = async (values: TGoogleMerchantSyncFormValues) => {
    if (!channelSlug) return;

    const idempotencyKey = uuidv4();

    const { mode } = values;

    const baseDto = {
      channelSlug,
      idempotencyKey,
    };

    switch (mode) {
      case GoogleMerchantSyncRunMode.FULL:
        await dispatch(syncGoogleMerchantFull(baseDto)).unwrap();
        break;

      case GoogleMerchantSyncRunMode.DELTA:
        const deltaDto = { ...baseDto, since: values.since?.toISOString() };

        await dispatch(syncGoogleMerchantDelta(deltaDto)).unwrap();
        break;

      case GoogleMerchantSyncRunMode.PRODUCT:
        const productDto = {
          ...baseDto,
          sku: values.sku,
        };

        await dispatch(syncGoogleMerchantProduct(productDto)).unwrap();
        break;

      default:
        return;
    }

    onClose();
  };

  return (
    <Modal destroyOnClose title={t('run_sync_title')} onCancel={onClose} visible={isOpen} size={ModalSize.MEDIUM}>
      <GoogleMerchantSyncForm
        onSubmit={handleRunSync}
        onReset={onClose}
        isLoading={isLoading}
        initialValues={{ [GoogleMerchantSyncFormFields.MODE]: GoogleMerchantSyncRunMode.FULL }}
      />
    </Modal>
  );
};

export default GoogleMerchantSyncModal;
